import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { money, STATUS_STYLES } from '@/lib/helpers';

/**
 * Barcode/product input with live suggestions dropdown (My Bill Book style).
 * Props:
 *  - storeId, statusFilter (default AVAILABLE)
 *  - onSelect(garment) : called when a suggestion is tapped
 *  - onSubmit(code)    : called on Enter with the raw typed text
 *  - placeholder, testId
 */
export default function BarcodeAutocomplete({ storeId = '', statusFilter = 'AVAILABLE',
  onSelect, onSubmit, placeholder = 'Scan / type code', testId = 'barcode-autocomplete', autoFocus = false }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!q || q.trim().length < 1) { setResults([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const base = { q: q.trim(), status: statusFilter, limit: 8 };
        let r = await api.get('/garments', { params: { ...base, store_id: storeId } });
        // Fallback: if nothing matched in the current store, search all stores so the
        // user always sees matches as they type (store is validated when adding).
        if ((!r.data.data || r.data.data.length === 0) && storeId) {
          r = await api.get('/garments', { params: base });
        }
        setResults(r.data.data || []); setOpen(true);
      } catch (e) { setResults([]); setOpen(true); }
      finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(t);
  }, [q, storeId, statusFilter]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const submit = () => {
    if (!q.trim()) return;
    onSubmit?.(q.trim());
    setQ(''); setResults([]); setOpen(false);
    inputRef.current?.focus();
  };
  const pick = (g) => {
    onSelect?.(g);
    setQ(''); setResults([]); setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className="relative" ref={boxRef}>
      <Input ref={inputRef} data-testid={testId} value={q} autoFocus={autoFocus}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => { if (results.length) setOpen(true); }}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        placeholder={placeholder} className="h-12 rounded-xl uppercase" autoComplete="off" />
      {open && (
        <div className="absolute z-[200] mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-border bg-popover shadow-lg"
          data-testid={`${testId}-suggestions`}>
          {loading && results.length === 0 && <p className="px-3 py-3 text-sm text-muted-foreground">Searching…</p>}
          {!loading && results.length === 0 && <p className="px-3 py-3 text-sm text-muted-foreground">No matching garments.</p>}
          {results.map((g) => (
            <button key={g.id} type="button" data-testid={`${testId}-option-${g.barcode}`} onClick={() => pick(g)}
              className="flex w-full items-center justify-between gap-2 border-b border-border px-3 py-2.5 text-left last:border-0 hover:bg-accent/60">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{g.product || '(no name)'}</p>
                <p className="font-mono text-xs text-muted-foreground">{g.barcode} · {g.size || '-'} · {g.colour || '-'}</p>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <span className="font-mono text-sm font-semibold">{money(g.price)}</span>
                <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium ${STATUS_STYLES[g.status] || 'bg-slate-100'}`}>{g.status}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
